import {CurrencyPipe} from '@angular/common';
import {Inject, Injectable, LOCALE_ID} from '@angular/core';
import {handleHttpError, ProtectionApiService, TranslationService} from '@emma-labs/shared/angular';
import {
  CategoryCode,
  Coverage,
  Dropdown,
  ErrorResponseCode,
  JobRiskClass,
  Protection,
  ProviderId,
  removeRiderFromCoverageBasedOnErrorCodesInPlace,
  Rider,
  RiderConfig,
  Sex,
  Version,
} from '@emma-labs/shared/typescript';
import range from 'lodash/range';
import {catchError, mergeMap, Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {AuthService} from './auth.service';

@Injectable({
  providedIn: 'root',
})
export class ProtectionService {
  currencyPipe: CurrencyPipe;

  constructor(
    private readonly api: ProtectionApiService,
    private readonly authService: AuthService,
    private readonly translate: TranslationService,
    @Inject(LOCALE_ID) locale: string,
  ) {
    this.currencyPipe = new CurrencyPipe(locale);
  }

  getProtections(
    ratesVersion: Version,
    coverages: Coverage[],
    providerId: ProviderId,
    categoryCodes: CategoryCode[],
    age: number,
    smoker: boolean,
    sex: Sex,
    extraConfig?: {
      surchargeRate?: number;
      jobRiskClass?: JobRiskClass;
      withRenewalPrices?: boolean;
      allowPartialSuccess?: boolean;
    },
  ): Observable<Protection[]> {
    return this.authService.currentAgentSessionOrAuthError.pipe(
      mergeMap((session) => {
        const protectionConfigs = coverages.map((coverage) => {
          return ProtectionApiService.buildProtectionConfig(
            ratesVersion,
            providerId,
            categoryCodes,
            age,
            sex,
            smoker,
            coverage,
            extraConfig,
          );
        });

        return this.api.getProtections(
          session.id,
          {protectionConfigs},
          extraConfig?.withRenewalPrices,
          extraConfig?.allowPartialSuccess,
        );
      }),
      map(({protections}) => protections),
    );
  }

  getRiders(
    ratesVersion: Version,
    providerId: ProviderId,
    riderConfigs: RiderConfig[],
    extraConfig?: {
      age?: number;
      smoker?: boolean;
      sex?: Sex;
      jobRiskClass?: JobRiskClass;
    },
  ): Observable<Rider[]> {
    return this.authService.currentAgentSessionOrAuthError.pipe(
      mergeMap((session) => {
        return this.api.getRiders(session.id, {
          providerConfig: {id: providerId, jobRiskClass: extraConfig?.jobRiskClass},
          riderConfigs,
          ratesVersion,
          age: extraConfig?.age,
          sex: extraConfig?.sex,
          smoker: extraConfig?.smoker,
        });
      }),
      map(({riders}) => riders),
    );
  }

  getSurchargedProtection(
    ratesVersion: Version,
    coverage: Coverage,
    category: CategoryCode,
    surchargeRate: number,
    age: number,
    smoker: boolean,
    sex: Sex,
    jobRiskClass?: JobRiskClass,
  ): Observable<Protection> {
    return this.authService.currentAgentSessionOrAuthError.pipe(
      mergeMap((session) =>
        this.api.getProtections(
          session.id,
          {
            protectionConfigs: [
              ProtectionApiService.buildProtectionConfig(
                ratesVersion,
                ProviderId.HUMANIA_EMMA_V2,
                [category],
                age,
                sex,
                smoker,
                coverage,
                {surchargeRate, jobRiskClass},
              ),
            ],
          },
          false,
        ),
      ),
      catchError((e) => {
        if (e.codes) {
          return this.alterCoverageAndGetProtections(
            ratesVersion,
            e.codes,
            coverage,
            category,
            surchargeRate,
            age,
            smoker,
            sex,
            jobRiskClass,
          );
        }
        return handleHttpError(e);
      }),
      map(({protections}) => protections[0]),
    );
  }

  // TODO: change return type to Dropdown<number>[] and remove canSelectNone when all coverage builders are updated
  // Also remove typecasting where this method is called
  getCoverageDropdownOptions(
    minCoverage: number,
    interval: number,
    maxCoverage: number,
    canSelectNone: boolean,
  ): Dropdown<number | undefined>[] {
    const dropdownOptions: Dropdown<number | undefined>[] = [
      ...range(minCoverage, maxCoverage + 1, interval).map(
        (coverage): Dropdown<number> => ({
          name: this.currencyPipe.transform(coverage, 'CAD', 'symbol-narrow', '1.0-0') ?? '',
          value: coverage,
        }),
      ),
    ];

    if (canSelectNone) {
      dropdownOptions.unshift({name: this.translate.getTranslation('NONE') ?? '', value: undefined});
    }

    return dropdownOptions;
  }

  private alterCoverageAndGetProtections(
    ratesVersion: Version,
    codes: ErrorResponseCode[],
    coverage: Coverage,
    category: CategoryCode,
    surchargeRate: number,
    age: number,
    smoker: boolean,
    sex: Sex,
    jobRiskClass?: JobRiskClass,
  ) {
    removeRiderFromCoverageBasedOnErrorCodesInPlace(codes, coverage);

    return this.authService.currentAgentSessionOrAuthError.pipe(
      mergeMap((session) =>
        this.api.getProtections(
          session.id,
          {
            protectionConfigs: [
              ProtectionApiService.buildProtectionConfig(
                ratesVersion,
                ProviderId.HUMANIA_EMMA_V2,
                [category],
                age,
                sex,
                smoker,
                coverage,
                {surchargeRate, jobRiskClass},
              ),
            ],
          },
          false,
        ),
      ),
    );
  }
}
